import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { z } from 'zod'
import { Button, PasswordInput, Stack, Alert } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconAlertCircle } from '@tabler/icons-react'
import AuthLayout from './AuthLayout'
import Footer from '../common/components/footer/footer'
import Meta from '../common/Meta'
import { useCompletePasswordReset } from './api'
import { ReactComponent as LeaguestarLogo } from '../assets/leaguestar_logo.svg'
import classes from './Auth.module.css'
import { zodResolver } from 'mantine-form-zod-resolver'

export default function PasswordResetPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const {
    mutate: completePasswordReset,
    isPending,
    error,
  } = useCompletePasswordReset()

  const form = useForm<ResetPasswordForm>({
    initialValues: emptyResetPasswordForm,
    validate: zodResolver(ResetPasswordFormSchema),
  })

  useEffect(() => {
    if (!token) {
      navigate('/forgot-password')
    }
  }, [token, navigate])

  const handleSubmit = (values: ResetPasswordForm) => {
    if (!token) return

    completePasswordReset(
      { token, password: values.new_password },
      {
        onSuccess: () => {
          navigate('/login?passwordReset=true')
        },
      }
    )
  }

  if (!token) {
    return null
  }

  return (
    <AuthLayout>
      <Meta title="Password reset" />
      <div className={classes.auth_container}>
        <div className={classes.auth_wrapper}>
          <LeaguestarLogo className={classes.leaguestar_logo} />
          <form
            className={classes.form_container}
            onSubmit={form.onSubmit(handleSubmit)}
          >
            <Stack justify="center">
              {error && (
                <Alert icon={<IconAlertCircle size="1rem" />} color="red">
                  {error.message}
                </Alert>
              )}

              <PasswordInput
                placeholder="New password"
                type="password"
                classNames={{
                  input: classes.passwordInput,
                  error: classes.error,
                }}
                {...form.getInputProps('new_password')}
                disabled={isPending}
              />
              <PasswordInput
                placeholder="Confirm password"
                type="password"
                classNames={{
                  input: classes.passwordInput,
                  error: classes.error,
                }}
                {...form.getInputProps('confirm_password')}
                disabled={isPending}
              />
              <Button
                className={classes.button}
                type="submit"
                loading={isPending}
                disabled={isPending}
              >
                Reset password
              </Button>
            </Stack>
          </form>
        </div>
      </div>
      <Footer />
    </AuthLayout>
  )
}

const ResetPasswordFormSchema = z
  .object({
    new_password: z
      .string()
      .min(8, 'Password must be at least 8 characters long.')
      .default(''),
    confirm_password: z
      .string()
      .min(8, 'Password must be at least 8 characters long.')
      .default(''),
  })
  .strict()
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'Passwords must match.',
    path: ['confirm_password'],
  })

const emptyResetPasswordForm: ResetPasswordForm = {
  new_password: '',
  confirm_password: '',
}

type ResetPasswordForm = z.infer<typeof ResetPasswordFormSchema>
